import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';

import WinnersBottomWaveDesktop from '../../svg/Waves/winners-bottom-wave-desktop.svg';
import WinnersBottomWaveMobile from '../../svg/Waves/winners-bottom-wave-mobile.svg';
import WinnersTopWaveDesktop from '../../svg/Waves/winners-top-wave-desktop.svg';
import WinnersTopWaveMobile from '../../svg/Waves/winners-top-wave-mobile.svg';
import BallMachineDesktop from './BallMachineDesktop.svg';
import BallMachineMobile from './BallMachineMobile.svg';
import ChoosePrizeDesktop from './ChoosePrizeDesktop.svg';
import ChoosePrizeMobile from './ChoosePrizeMobile.svg';
import ChooseYourNumbersDesktop from './ChooseYourNumbersDesktop.svg';
import ChooseYourNumbersMobile from './ChooseYourNumbersMobile.svg';
import styles from './HowItWorksV2.module.css';

interface AnimationData {
  [key: string]: unknown;
}

interface Props {
  background: 'white' | 'darkBlue';
}

const HowItWorksV2 = observer(({ background }: Props) => {
  const userStore = useUserStore();

  const showEnterButton = !userStore.subscriptionStatusInfo.subscriberStatus;

  return (
    <div>
      {background === 'darkBlue' && (
        <>
          <div className={clsx(styles.topWave, styles.topWaveDesktop)}>
            <WinnersTopWaveDesktop />
          </div>
          <div className={clsx(styles.topWave, styles.topWaveMobile)}>
            <WinnersTopWaveMobile />
          </div>
        </>
      )}
      <AboutUsModule
        background={background}
        withNoPadding={background === 'darkBlue'}
      >
        <AboutUsSiteContainer styles2026>
          <div
            className={clsx(
              styles.howItWorks,
              background === 'darkBlue' && styles.backgroundDarkBlue,
            )}
          >
            <FadeIn onScroll delay={0.05} className={styles.introHeading}>
              <div className={styles.headingContainer}>
                <div className={styles.headingText}>
                  <p className="daymadeTypographyTaglineAccent">
                    In 3 simple steps
                  </p>
                  <h2 className={'daymadeTypographyXL'}>How it works</h2>
                </div>
                {showEnterButton && (
                  <div className="desktopOnly">
                    <EnterButton
                      overrideButtonText="Enter to Win"
                      overrideReferralText
                      dm2026Button
                      buttonSize="Large"
                      minWidth={240}
                    />
                  </div>
                )}
              </div>
            </FadeIn>
            <div className={styles.stepsWrapper}>
              <FadeIn
                onScroll
                className={clsx(styles.step, styles.step1)}
                delay={0.1}
              >
                <div className={styles.stepImage}>
                  <ChooseYourNumbersMobile className="mobileOnly" />
                  <ChooseYourNumbersDesktop
                    className="desktopOnly"
                    width={141}
                    height={116}
                  />
                </div>
                <div className={styles.stepText}>
                  <div
                    className={clsx(
                      'daymadeTypographyMBlack',
                      styles.stepTitle,
                    )}
                  >
                    1. Pick your numbers
                  </div>
                  <div
                    className={clsx(
                      'daymadeTypographyMRegular',
                      styles.stepDescription,
                    )}
                  >
                    Choose your lucky numbers
                  </div>
                </div>
              </FadeIn>

              <FadeIn
                onScroll
                delay={0.35}
                className={clsx(styles.step, styles.step2)}
              >
                <div className={styles.stepImage}>
                  <BallMachineMobile className="mobileOnly" />
                  <BallMachineDesktop
                    className="desktopOnly"
                    width={186}
                    height={103}
                  />
                </div>
                <div className={styles.stepText}>
                  <div
                    className={clsx(
                      'daymadeTypographyMBlack',
                      styles.stepTitle,
                    )}
                  >
                    2. Match to win
                  </div>
                  <div
                    className={clsx(
                      'daymadeTypographyMRegular',
                      styles.stepDescription,
                    )}
                  >
                    The more numbers you match, the bigger the prize
                  </div>
                </div>
              </FadeIn>

              <FadeIn
                onScroll
                delay={0.6}
                className={clsx(styles.step, styles.step3)}
              >
                <div className={styles.stepImage}>
                  <ChoosePrizeMobile className="mobileOnly" />
                  <ChoosePrizeDesktop
                    className="desktopOnly"
                    width={130}
                    height={109}
                  />
                </div>
                <div className={styles.stepText}>
                  <div
                    className={clsx(
                      'daymadeTypographyMBlack',
                      styles.stepTitle,
                    )}
                  >
                    3. Choose your prize
                  </div>
                  <div
                    className={clsx(
                      'daymadeTypographyMRegular',
                      styles.stepDescription,
                    )}
                  >
                    From bucket list holidays to treats that make your day
                  </div>
                </div>
              </FadeIn>
            </div>
            <FadeIn onScroll className={styles.buttonContainer}>
              {showEnterButton && (
                <EnterButton
                  overrideButtonText="Enter to Win"
                  overrideReferralText
                  dm2026Button
                  buttonSize="Large"
                  minWidth={240}
                />
              )}
            </FadeIn>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>
      {background === 'darkBlue' && (
        <>
          <div className={clsx(styles.bottomWave, styles.bottomWaveDesktop)}>
            <WinnersBottomWaveDesktop />
          </div>
          <div className={clsx(styles.bottomWave, styles.bottomWaveMobile)}>
            <WinnersBottomWaveMobile />
          </div>
        </>
      )}
    </div>
  );
});

export default HowItWorksV2;
